import type { Chart } from "../chart/Chart";

export interface Anchor {
  time: number; // unix seconds (absolute — persists across timeframes)
  price: number;
}

export interface DrawStyle {
  color: string;
  width: number;
  dash: number[] | null;
  fillAlpha: number; // 0 = no fill
  text?: string;
  fontSize?: number;
}

export interface Handle {
  x: number;
  y: number;
  kind: number; // handle index/role
}

export function defaultStyle(color: string): DrawStyle {
  return { color, width: 2, dash: null, fillAlpha: 0.12 };
}

let idCounter = 1;
export function nextId(): string {
  return "d" + (idCounter++).toString(36) + Date.now().toString(36).slice(-4);
}

export abstract class DrawingModel {
  id: string = nextId();
  abstract type: string;
  anchors: Anchor[] = [];
  style: DrawStyle;

  constructor(style: DrawStyle) {
    this.style = style;
  }

  // number of clicks/anchors placed during interactive creation (1 or 2)
  get points(): number {
    return 2;
  }

  abstract render(ctx: CanvasRenderingContext2D, chart: Chart, selected: boolean): void;
  abstract hitTest(px: number, py: number, chart: Chart): boolean;

  // draggable control points (default = the anchors themselves)
  handles(chart: Chart): Handle[] {
    return this.anchors.map((a, i) => ({ x: this.X(a, chart), y: this.Y(a, chart), kind: i }));
  }

  moveHandle(kind: number, px: number, py: number, chart: Chart): void {
    const a = this.anchors[kind];
    if (!a) return;
    a.time = chart.timeOfX(px);
    a.price = chart.viewport.priceOfY(py);
  }

  translate(dpx: number, dpy: number, chart: Chart): void {
    for (const a of this.anchors) {
      const x = this.X(a, chart) + dpx;
      const y = this.Y(a, chart) + dpy;
      a.time = chart.timeOfX(x);
      a.price = chart.viewport.priceOfY(y);
    }
  }

  // px helpers
  protected X(a: Anchor, chart: Chart): number {
    return chart.xOfTime(a.time);
  }
  protected Y(a: Anchor, chart: Chart): number {
    return chart.viewport.yOfPrice(a.price);
  }

  protected applyStroke(ctx: CanvasRenderingContext2D) {
    ctx.strokeStyle = this.style.color;
    ctx.lineWidth = this.style.width;
    ctx.setLineDash(this.style.dash ?? []);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }

  serialize(): any {
    return { id: this.id, type: this.type, anchors: this.anchors, style: this.style };
  }

  loadFrom(data: any) {
    this.id = data.id ?? this.id;
    this.anchors = data.anchors;
    this.style = { ...this.style, ...data.style };
  }
}
